import React, { useState } from 'react';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';

interface FAQItemProps {
  question: string;
  answer: string | React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <button
        className="w-full py-6 text-left flex items-center justify-between focus:outline-none"
        onClick={onClick}
      >
        <span className="text-lg font-semibold text-gray-900 dark:text-white">{question}</span>
        {isOpen ? (
          <HiChevronUp className="w-6 h-6 text-gray-500 dark:text-gray-400" />
        ) : (
          <HiChevronDown className="w-6 h-6 text-gray-500 dark:text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="pb-6 text-gray-600 dark:text-gray-300 space-y-2">
          {answer}
        </div>
      )}
    </div>
  );
};

export const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleClick = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "How secure is the XRP chat app?",
      answer: "The XRP chat app uses military-grade end-to-end encryption to protect your messages. All messages are encrypted on your device before being sent, and only the intended recipient can decrypt them. We use AES-256 encryption and XRPL blockchain technology for identity verification."
    },
    {
      question: "What is XRPL identity?",
      answer: "XRPL identity uses the XRP Ledger blockchain to create a secure, decentralised identity for each user. When you sign up, a unique wallet is generated that serves as your identity. This provides cryptographic security and allows for secure message encryption."
    },
    {
      question: "Can the XRP chat app read my messages?",
      answer: "No. The XRP chat app uses end-to-end encryption, which means messages are encrypted on your device and can only be decrypted by the intended recipient. We never have access to your decrypted messages or encryption keys."
    },
    {
      question: "How do I start a chat with someone?",
      answer: (
        <div className="space-y-2">
          <p>There are two ways to start a chat:</p>
          <ol className="list-decimal list-inside pl-4 space-y-1">
            <li>Share your QR code with someone to connect</li>
            <li>Enter their wallet address directly</li>
          </ol>
          <p>Once connected, you can start sending encrypted messages immediately.</p>
        </div>
      )
    },
    {
      question: "What happens if I lose my device?",
      answer: "Your private keys are stored securely on your device. If you lose your device, you'll need to create a new account and establish new connections. For security reasons, we cannot recover your encryption keys or decrypted messages."
    },
    {
      question: "Can you prove the messages are encrypted?",
      answer: "We have a toggle on/off decryption button in the settings section of the app. If you turn this off you will see the hash generated by your public key which you shared with the other chat partisipant. Now only you have the private key to decrypt it!"
    },
    {
      question: "How does the encryption work?",
      answer: (
        <div className="space-y-2">
          <p>The XRP chat app's encryption works in several steps:</p>
          <ol className="list-decimal list-inside pl-4 space-y-1">
            <li>Each user has a public and private key pair</li>
            <li>Messages are encrypted with the recipient's public key</li>
            <li>Only the recipient's private key can decrypt the message</li>
            <li>All encryption/decryption happens on your device</li>
          </ol>
        </div>
      )
    },
    {
      question: "Do you store my messages?",
      answer: "While encrypted messages are stored to enable message history and synchronisation across devices, we can never access the content of your messages because we don't have the decryption keys. Only you and your chat partners can read the messages."
    },
    {
      question: "How can I support the XRPchat project?",
      answer: (
        <div className="space-y-2">
          <p>There are several ways you can support the XRPchat project:</p>
          <ol className="list-decimal list-inside pl-4 space-y-1">
            <li>Make a cryptocurrency donation to help fund development and server costs</li>
            <li>Spread the word about XRPchat on social media</li>
            <li>Report bugs and provide feedback to help us improve</li>
          </ol>
          <p className="mt-2">
            Visit our <a href="/website/community" className="text-brand-primary hover:underline">Community page</a> to learn more about making donations and supporting our mission.
          </p>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-16">
      <section className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
          Frequently Asked Questions
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Find answers to common questions about the XRP chat <i>app</i>'s security, features, and usage.
        </p>
      </section>

      <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-md">
        <div className="p-8">
          {faqs.map((faq, index) => (
            <FAQItem 
              key={index} 
              question={faq.question} 
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => handleClick(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
